// @ts-nocheck
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Box, Button, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";
import { v4 as uuid } from "uuid";
import { getCssVariableHandler } from "../../../utils/styles/getCssVariables";
import { useDispatch } from "react-redux";
import { alertDialogAciton } from "../../../store/actions/auth";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

export const FileUpload = ({
  fileChangeHandler,
  text,
  accept,
  inputProps,
  sx,
  noBackground,
  acceptedFormats,
  disabled = false,
  sizeLimitInMB = null,
  multiple = false,
}) => {
  const uniqueId = uuid();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const dispatch = useDispatch();

  const validateAndHandleFiles = (files) => {
    if (!files || files.length === 0) return;

    const acceptedTypes = accept?.split(",").map((type) => type.trim()) || [];
    const validFiles = Array.from(files).filter((file) => {
      return acceptedTypes.some((acceptedType) => {
        if (acceptedType.endsWith("/*")) {
          const baseType = acceptedType.split("/")[0];
          return file.type.startsWith(baseType + "/");
        }
        return file.type === acceptedType;
      });
    });

    if (validFiles.length === 0) {
      dispatch(
        alertDialogAciton({
          open: true,
          title: "Error",
          message: `Invalid file type, please upload: ${acceptedFormats}`,
        }),
      );
      return;
    }

    if (sizeLimitInMB) {
      const maxSizeInBytes = sizeLimitInMB * 1024 * 1024;
      const oversizedFiles = validFiles.filter(
        (file) => file.size > maxSizeInBytes,
      );

      if (oversizedFiles.length > 0) {
        dispatch(
          alertDialogAciton({
            open: true,
            title: "Error",
            message: `File size exceeds ${sizeLimitInMB}MB limit`,
          }),
        );
        return;
      }
    }

    const newFiles = multiple ? [...selectedFiles, ...validFiles] : validFiles;
    setSelectedFiles(newFiles);

    const dt = new DataTransfer();
    newFiles.forEach((file) => dt.items.add(file));
    fileChangeHandler({ target: { files: dt.files } });
  };

  const handleRemoveFile = (indexToRemove) => {
    const updatedFiles = selectedFiles.filter(
      (_, index) => index !== indexToRemove,
    );
    setSelectedFiles(updatedFiles);

    const dt = new DataTransfer();
    updatedFiles.forEach((file) => dt.items.add(file));
    fileChangeHandler({ target: { files: dt.files } });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    validateAndHandleFiles(files);
  };

  const handleInputChange = (e) => {
    const files = e.target?.files;
    validateAndHandleFiles(files);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith("image/")) {
      return (
        <ImageIcon
          sx={{
            fontSize: 40,
            color: getCssVariableHandler({
              variableName: "--primary-pink-hex",
            }),
          }}
        />
      );
    } else if (file.type.startsWith("video/")) {
      return (
        <VideoLibraryIcon
          sx={{
            fontSize: 40,
            color: getCssVariableHandler({
              variableName: "--primary-pink-hex",
            }),
          }}
        />
      );
    } else if (file.type === "application/pdf") {
      return (
        <PictureAsPdfIcon
          sx={{
            fontSize: 40,
            color: getCssVariableHandler({
              variableName: "--primary-pink-hex",
            }),
          }}
        />
      );
    }
    return (
      <InsertDriveFileIcon
        sx={{
          fontSize: 40,
          color: getCssVariableHandler({ variableName: "--primary-pink-hex" }),
        }}
      />
    );
  };

  return (
    <Box>
      <label htmlFor={uniqueId}>
        <input
          type="file"
          id={uniqueId}
          accept={accept ?? "application/pdf"}
          onChange={handleInputChange}
          disabled={disabled}
          multiple={multiple}
          hidden
          {...(inputProps || {})}
        />
        <Box
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            padding: "2.5rem 6rem",
            borderRadius: "1rem",
            backgroundColor: "black",
            color: "white",
            width: "100%",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            "&:hover": {
              backgroundColor: "#111",
            },
            border: isDragging
              ? "2px dashed white"
              : `2px dashed ${noBackground ? "transparent" : getCssVariableHandler({ variableName: "--primary-pink-hex" })}`,
            transition: "border 0.2s ease",
            ...(sx || {}),
          }}
        >
          {!multiple && selectedFiles.length > 0 ? (
            // Single file selected view
            <>
              <Box
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveFile(0);
                }}
                sx={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  backgroundColor: "rgba(244, 67, 54, 0.2)",
                  borderRadius: "50%",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "rgba(244, 67, 54, 0.4)",
                  },
                  zIndex: 10,
                }}
              >
                <Typography
                  sx={{ color: "#f44336", fontSize: "1.25rem", lineHeight: 1 }}
                >
                  ✕
                </Typography>
              </Box>
              {getFileIcon(selectedFiles[0])}
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "1rem",
                  fontWeight: "500",
                  mt: 2,
                  textAlign: "center",
                  maxWidth: "80%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedFiles[0].name}
              </Typography>
              <Typography
                sx={{
                  color: "#999",
                  fontSize: "0.875rem",
                  mt: 0.5,
                }}
              >
                {formatFileSize(selectedFiles[0].size)}
              </Typography>
            </>
          ) : (
            // Default upload view
            <>
              <CloudUploadIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography
                sx={{
                  color: getCssVariableHandler({
                    variableName: "--primary-pink-hex",
                  }),
                  fontSize: "1.15rem",
                }}
              >
                {text ?? "Upload pdf file here"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  marginTop: 1,
                  opacity: "0.75 !important",
                }}
              >
                or drop files here
              </Typography>
              {acceptedFormats && (
                <Typography
                  sx={{ fontSize: "0.75rem", opacity: "0.75 !important" }}
                >
                  Accepted Formats: {acceptedFormats}
                </Typography>
              )}
            </>
          )}
        </Box>
      </label>

      {/* Multiple Selected Files List */}
      {selectedFiles.length > 0 && multiple && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ color: "#fff", fontSize: "0.875rem", mb: 1 }}>
            Selected Files ({selectedFiles.length}):
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {selectedFiles.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#111",
                  padding: "0.75rem 1rem",
                  borderRadius: "0.5rem",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <Box sx={{ flex: 1, overflow: "hidden" }}>
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: "0.875rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#999",
                      fontSize: "0.75rem",
                    }}
                  >
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
                <Button
                  onClick={() => handleRemoveFile(index)}
                  sx={{
                    minWidth: "auto",
                    padding: "0.25rem 0.75rem",
                    color: "#f44336",
                    fontSize: "0.75rem",
                    backgroundColor: "transparent",
                    border: "none",
                    "&:hover": {
                      backgroundColor: "rgba(244, 67, 54, 0.1)",
                    },
                  }}
                >
                  Remove
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const FileUploadSmall = ({
  fileChangeHandler,
  text,
  accept,
  inputProps,
  sx,
  noBackground,
  acceptedFormats,
  isLoading,
}) => {
  const uniqueId = uuid();
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      fileChangeHandler({ target: { files } });
    }
  };

  return (
    <label htmlFor={uniqueId}>
      <input
        type="file"
        id={uniqueId}
        accept={accept ?? "application/pdf"}
        onChange={fileChangeHandler}
        hidden
        {...(inputProps || {})}
      />
      <Box
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          padding: "0.25rem 1rem",
          borderRadius: "0.5rem",
          backgroundColor: "black",
          color: "white",
          width: "100%",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          "&:hover": {
            backgroundColor: "#111",
          },
          border: isDragging
            ? "1px solid white"
            : `1px solid ${noBackground ? "transparent" : getCssVariableHandler({ variableName: "--primary-pink-hex" })}`,
          transition: "border 0.2s ease",
          ...(sx || {}),
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              width: "2.25rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div className="flat-loader"></div>
          </Box>
        ) : (
          <Typography
            sx={{
              color: getCssVariableHandler({
                variableName: "--primary-pink-hex",
              }),
              fontSize: "0.8rem",
            }}
          >
            {"Upload"}
          </Typography>
        )}
      </Box>
    </label>
  );
};

FileUpload.propTypes = {
  fileChangeHandler: PropTypes.func,
  text: PropTypes.string,
  accept: PropTypes.string,
  sx: PropTypes.object,
  inputProps: PropTypes.object,
  noBackground: PropTypes.bool,
  acceptedFormats: PropTypes.string,
  disabled: PropTypes.bool,
  sizeLimitInMB: PropTypes.number,
  multiple: PropTypes.bool,
};

FileUploadSmall.propTypes = {
  fileChangeHandler: PropTypes.func,
  text: PropTypes.string,
  accept: PropTypes.string,
  sx: PropTypes.object,
  inputProps: PropTypes.object,
  noBackground: PropTypes.bool,
  acceptedFormats: PropTypes.string,
  isLoading: PropTypes.bool,
};